<template>
  <b-modal
    :id="modalId"
    size="xl"
    title="Unificar comandas duplicadas"
    no-fade
    no-close-on-backdrop
    no-close-on-esc
    @show="onShow"
    @hidden="onHidden"
  >
    <div v-if="!itens || itens.length < 2" class="alert alert-warning">
      Selecione pelo menos 2 comandas para unificar.
    </div>

    <div v-else>
      <p class="text-muted">
        Escolha a comanda <strong>destino</strong>. Todas as outras terão seus
        pedidos, pagamentos e itens de caixa movidos para o destino e serão
        removidas. Comandas em mesas/sessões diferentes são suportadas; mesas
        que ficarem sem comandas serão fechadas automaticamente.
      </p>

      <div class="row">
        <div
          v-for="it in itens"
          :key="it.comanda.id"
          class="col-12 col-md-6 col-lg-4 mb-3"
        >
          <div
            class="card h-100"
            :class="{ 'border-success border-2 shadow': it.comanda.id === codDestino }"
          >
            <div class="card-body">
              <div class="form-check mb-2">
                <input
                  class="form-check-input"
                  type="radio"
                  :id="'destino_comanda_' + it.comanda.id"
                  :value="it.comanda.id"
                  v-model="codDestino"
                />
                <label
                  class="form-check-label font-weight-bold"
                  :for="'destino_comanda_' + it.comanda.id"
                >
                  Usar como DESTINO
                </label>
              </div>
              <hr class="my-2" />
              <h6 class="mb-1">
                <i class="fa fa-barcode mr-1"></i>{{ getNome(it.comanda, 0) }}
              </h6>
              <small class="d-block" v-if="getNome(it.comanda, 1).trim() != ''">
                <strong>Cliente:</strong> {{ getNome(it.comanda, 1) }}
              </small>
              <small class="d-block" v-if="getNome(it.comanda, 2).trim() != ''">
                <strong>Tel:</strong> {{ getNome(it.comanda, 2) }}
              </small>
              <small class="d-block">
                <strong>Mesa:</strong> {{ it.mesa.mesa || it.mesa.cod_mesa }}
                <span class="text-muted">
                  (aberta {{ it.mesa.dt_aberta ? $moment(it.mesa.dt_aberta).format('DD/MM HH:mm') : '—' }})
                </span>
              </small>
              <small class="d-block">
                <strong>Total:</strong> {{ formatFloatToReal(it.comanda.vl_total || 0) }}
                · <strong>Pago:</strong> {{ formatFloatToReal(it.comanda.vl_pago || 0) }}
              </small>
              <small class="d-block text-muted">#comanda {{ it.comanda.id }}</small>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-2 p-3 bg-light rounded">
        <h6 class="mb-2">Resultado</h6>
        <ul class="mb-2">
          <li>
            Destino: <strong>{{ destinoLabel }}</strong>
          </li>
          <li>
            {{ origens.length }} comanda(s) de origem serão removidas e seu
            conteúdo movido para o destino.
          </li>
          <li>
            Total somado no destino:
            <strong>{{ formatFloatToReal(totalSomado) }}</strong>
          </li>
        </ul>
        <small class="text-danger">
          <strong>Atenção:</strong> esta operação é irreversível.
        </small>
      </div>
    </div>

    <template #modal-footer>
      <b-btn variant="secondary" @click="cancelar" :disabled="executando">Cancelar</b-btn>
      <b-btn
        variant="success"
        :disabled="!codDestino || executando || !itens || itens.length < 2"
        @click="confirmar"
      >
        <b-spinner small v-if="executando" /> Unificar
      </b-btn>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: "ComandaUnificarModal",
  props: {
    modalId: { type: String, default: "modal-unificar-comandas" },
    // Array de itens { mesa, comanda } vindos da listagem de comandas.
    itens: { type: Array, default: () => [] },
  },
  data() {
    return {
      codDestino: null,
      executando: false,
      aguardando: false,
    };
  },
  sockets: {
    "mesas:unificarComandasResult": function (result) {
      if (!this.aguardando) return;
      this.aguardando = false;
      this.executando = false;
      if (result && result.success) {
        this.showToast("success", "Comandas unificadas");
        this.$emit("unificado", result.data);
        this.$bvModal.hide(this.modalId);
      } else {
        this.$swal.fire({
          title: "Não foi possível unificar",
          text: (result && result.message) || "Erro desconhecido",
          icon: "error",
        });
      }
    },
  },
  computed: {
    origens() {
      if (!this.itens) return [];
      return this.itens
        .map((it) => it.comanda.id)
        .filter((id) => id && id !== this.codDestino);
    },
    destinoItem() {
      return (this.itens || []).find((it) => it.comanda.id === this.codDestino);
    },
    destinoLabel() {
      if (!this.destinoItem) return "—";
      const nome = this.getNome(this.destinoItem.comanda, 0).trim();
      const cliente = this.getNome(this.destinoItem.comanda, 1).trim();
      return cliente ? `${nome} | ${cliente}` : nome;
    },
    totalSomado() {
      return (this.itens || []).reduce(
        (acc, it) => acc + Number(it.comanda.vl_total || 0),
        0
      );
    },
  },
  methods: {
    getNome(comanda, index) {
      if (comanda && comanda.nome && comanda.nome.includes("|")) {
        return comanda.nome.split("|")[index] || "";
      }
      return index === 0 && comanda ? comanda.nome || "" : "";
    },
    escolherDestinoPadrao() {
      if (!this.itens || this.itens.length === 0) return null;
      // Destino padrão = comanda da sessão aberta mais recentemente.
      const ordenados = [...this.itens].sort((a, b) => {
        // $moment em vez de new Date (datetime com espaço quebra no WebKit/iOS).
        const da = a.mesa && a.mesa.dt_aberta ? this.$moment(a.mesa.dt_aberta).valueOf() : 0;
        const db = b.mesa && b.mesa.dt_aberta ? this.$moment(b.mesa.dt_aberta).valueOf() : 0;
        if (db !== da) return db - da;
        return (b.comanda.id || 0) - (a.comanda.id || 0);
      });
      return ordenados[0].comanda.id;
    },
    onShow() {
      this.executando = false;
      this.aguardando = false;
      this.codDestino = this.escolherDestinoPadrao();
    },
    onHidden() {
      this.codDestino = null;
      this.executando = false;
      this.aguardando = false;
    },
    cancelar() {
      this.$bvModal.hide(this.modalId);
    },
    async confirmar() {
      if (!this.codDestino || !this.destinoItem || this.origens.length === 0) return;
      const confirm = await this.$swal.fire({
        title: "Confirmar unificação?",
        html:
          "Mover <b>" +
          this.origens.length +
          "</b> comanda(s) para <b>" +
          this.destinoLabel +
          "</b>.<br><small class='text-danger'>Esta operação é irreversível.</small>",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, unificar",
        cancelButtonText: "Cancelar",
      });
      if (!confirm.isConfirmed) return;

      const mesa = this.destinoItem.mesa || {};
      this.executando = true;
      this.aguardando = true;
      this.$socket.emit("mesas:unificarComandas", {
        comanda_destino: this.codDestino,
        comandas_origem: this.origens,
        cod_empresa: mesa.cod_empresa || (this.user && this.user.cod_empresa),
        dt_aberta: mesa.dt_aberta,
      });
    },
  },
};
</script>
